import { IS_DEBUG } from '@/client/confs/app';
import { RouteItem } from '@/client/confs/routes';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { matchRoute, Parser } from 'wouter';

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

// Map contract error selectors & common error patterns to user-friendly messages
const CONTRACT_ERROR_MAP: Record<string, string> = {
  '0x1270fbd3': 'Slippage too high. Try increasing slippage tolerance or reducing trade size.',
  '0xcf479181': 'Insufficient balance for this trade.',
  '0xbb55fd27': 'Insufficient liquidity in the pool.',
  '0xc2f5625a': 'Amount too small. Please increase trade size.',
  '0xb521771a': 'This market is not active.',
  '0x203d82d8': 'This market has expired.',
  '0x5284b85f': 'Withdraw amount exceeds available balance.',
  '0xfb8f41b2': 'Transfer failed. Please check token approval.',
  '0x2c5211c6': 'Invalid amount.',
  '0x03dee4c5': 'Insufficient token balance. Your position may have already been sold or redeemed.',
};

const ERROR_PATTERN_MAP: [RegExp, string][] = [
  [/insufficient funds/i, 'Insufficient BNB for gas fees. Please top up your wallet.'],
  [/insufficient balance/i, 'Insufficient balance for this transaction.'],
  [/gas required exceeds/i, 'Transaction requires more gas than allowed. Try a smaller amount.'],
  [/user rejected/i, 'Transaction was cancelled.'],
  [/user denied/i, 'Transaction was cancelled.'],
  [/nonce too low/i, 'Transaction conflict. Please wait and try again.'],
  [/replacement.*underpriced/i, 'A pending transaction exists. Please wait for it to complete.'],
  // Claim-specific errors (PredictionCTF / CTF)
  [/payout.*denominator/i, 'This market has already been settled.'],
  [/payouts.*not.*set/i, 'Settlement not complete yet. Please try again later.'],
  [/already.*redeemed/i, 'You have already claimed your winnings.'],
  [/condition.*not.*found/i, 'Market data error. Please contact support.'],
  [/result.*not.*set/i, 'Settlement not complete yet. Please try again later.'],
  // Generic fallbacks (keep last)
  [/execution reverted/i, 'Transaction failed. The contract rejected this operation.'],
  [/network.*error/i, 'Network error. Please check your connection and try again.'],
  [/timeout/i, 'Request timed out. Please try again.'],
];

export const getErrorMessage = (err: any): string | undefined => {
  if (IS_DEBUG) console.error(err);

  const raw = err?.shortMessage || err?.message || '';

  // Check for contract error selectors (e.g., "0x1270fbd3")
  const selectorMatch = raw.match(/0x[0-9a-f]{8}/i);
  if (selectorMatch) {
    const friendly = CONTRACT_ERROR_MAP[selectorMatch[0].toLowerCase()];
    if (friendly) return friendly;
  }

  // Check for common error patterns
  for (const [pattern, message] of ERROR_PATTERN_MAP) {
    if (pattern.test(raw)) return message;
  }

  // Fallback: return cleaned message (remove technical prefix)
  const cleaned = raw
    .replace(/^The contract function "\w+" reverted.*?:?\s*/i, '')
    .replace(/^ContractFunctionRevertedError:\s*/i, '')
    .trim();
  return cleaned || undefined;
};

export const findMatchingRoute = (routes: RouteItem[], parser: Parser, location: string, parent = ''): RouteItem | null => {
  for (const route of routes) {
    const path = parent + route.path;
    if (path === '*') return route;
    if (path === '/' ? path === location : matchRoute(parser, path, location)) {
      return route;
    }
    if (route.children && route.children?.length > 0) {
      const matchedChildRoute = findMatchingRoute(route.children, parser, location, path);
      if (matchedChildRoute) {
        return matchedChildRoute;
      }
    }
  }
  return null;
};
